/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { Middleware } from "redux"
import notification from "components/common/notification"
import { compact } from "lodash"
import { ResponseWithError } from "types/api"
import { resolveGenericForbiddenMessage } from "utils/locale"
import { getDalErrMessage, parseJSONBasedErrorMsg } from "utils/misc"
import { AppState } from "../store.type"
import { errorTargetDebugWhitelist } from "./constants"

export const showErrorMiddleware: Middleware<
  {},
  AppState
> = store => next => action => {
  const previousAction = action?.meta?.previousAction

  if (
    previousAction?.showError === true &&
    (`${previousAction?.type}_FAIL` === action.type ||
      `${previousAction?.type}_FAILED` === action.type)
  ) {
    const options = previousAction?.errorOptions ?? {}

    const data = action?.error?.response?.data
    const status = action?.error?.response?.status

    const defaultMessage =
      store.getState()?.global?.messages?.error ?? JSON.stringify(data)
    const callbackValue = previousAction?.errorCallback?.(data) ?? true
    if (callbackValue === false) return next(action)

    let message = callbackValue === true ? defaultMessage : callbackValue

    const isMenuGroupRequest = (
      previousAction?.payload?.request?.url ?? ""
    ).includes("menu-group")
    if (isMenuGroupRequest && !previousAction?.errorCallback && data) {
      const errResp: ResponseWithError = data as ResponseWithError
      const { message: msg = "" } = errResp

      const dalMsg = getDalErrMessage(msg)
      if (dalMsg) {
        message = dalMsg
      }

      const jsonBasedMsg = parseJSONBasedErrorMsg(msg)
      if (jsonBasedMsg) {
        message = jsonBasedMsg
      }
    }
    if (
      action?.error?.response?.status === 403 &&
      action?.error?.response?.data?.message
    ) {
      message = action?.error?.response?.data?.message
    }
    // print BE error responses in default message for easier debugging
    let description: {
      [key: string]: string
    } = {
      trace: action?.error?.response?.headers?.["x-grabkit-grab-requestid"],
    }
    if (
      callbackValue === true &&
      errorTargetDebugWhitelist.includes(data?.target)
    ) {
      description = {
        ...description,
        status,
        message: data?.message,
        reason: data?.reason,
        target: data?.target,
      }
    }

    const notify = (toastMessage: string) =>
      notification.error({
        message: toastMessage,
        description: compact(
          Object.keys(description).map((key: string) =>
            description[key] ? `${key}=${description[key]}` : null,
          ),
        ).join(", "),
        key: toastMessage, // to prevent duplicate message at the same time
        ...options,
      })

    // The generic BE 403 message ("Forbidden") is not localized; swap in the
    // unauthorized-access copy, resolved asynchronously because middleware
    // runs outside React.
    const localizedForbidden = resolveGenericForbiddenMessage(
      status,
      data?.message,
    )
    if (localizedForbidden) {
      localizedForbidden.then(notify)
      return next(action)
    }

    notify(message)
  }
  return next(action)
}
